/**
 * B1.7 Cashier feature — mock↔live API dispatcher.
 *
 * Runtime switch driven by {@link APP_ENV.bizApiMode}:
 *   - `mock` (default): serves deterministic seed rows from
 *     {@link ./cashier-api-mock} (Lovable / Storybook / pre-wire sessions).
 *   - `live`: routes through {@link ./cashier-api-http} → `bizClient` →
 *     `/api/biz/cashier/*` on the backend.
 *
 * The dispatcher is resolved at module load so React Query cache keys,
 * hook signatures, and the frozen-contract `CashierSnapshot` shape stay
 * identical regardless of the mode. Flipping `VITE_BIZ_API_MODE` (or
 * `import.meta.env.VITE_BIZ_API_MODE` at build time) is enough to graduate
 * the feature from fixtures to real endpoints — no hook rewrites, no
 * downstream churn.
 *
 * Parallels the `invoice-api.ts` dispatcher shipped in the Phase D FE rewire.
 *
 * @see cashier-api-mock.ts
 * @see cashier-api-http.ts
 * @see docs/_context/PETOTA_BUSINESS_IMPLEMENTATION_PLAN.md §Phase C2 FE rewire
 */

import { APP_ENV } from '@/shared/config/env';

import * as mockApi from './cashier-api-mock';
import * as httpApi from './cashier-api-http';

import type {
  AddCartItemRequest,
  ApplyDiscountRequest,
  CashierSnapshot,
  CloseSessionRequest,
  CommitTenderRequest,
  CommitTenderResult,
  GetCashierSnapshotParams,
  ParkOrderRequest,
  RemoveCartItemRequest,
  ResumeParkedRequest,
  UpdateCartQtyRequest,
  VoidOrderRequest,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function getCashierSnapshot(
  params: GetCashierSnapshotParams,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.getCashierSnapshot(params) : mockApi.getCashierSnapshot(params);
}

export async function addCartItem(
  req: AddCartItemRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.addCartItem(req) : mockApi.addCartItem(req);
}

export async function updateCartQty(
  req: UpdateCartQtyRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.updateCartQty(req) : mockApi.updateCartQty(req);
}

export async function removeCartItem(
  req: RemoveCartItemRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.removeCartItem(req) : mockApi.removeCartItem(req);
}

export async function applyDiscount(
  req: ApplyDiscountRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.applyDiscount(req) : mockApi.applyDiscount(req);
}

export async function parkOrder(
  req: ParkOrderRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.parkOrder(req) : mockApi.parkOrder(req);
}

export async function resumeParked(
  req: ResumeParkedRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.resumeParked(req) : mockApi.resumeParked(req);
}

export async function voidOrder(
  req: VoidOrderRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.voidOrder(req) : mockApi.voidOrder(req);
}

export async function commitTender(
  req: CommitTenderRequest,
): Promise<CommitTenderResult> {
  return useLive ? httpApi.commitTender(req) : mockApi.commitTender(req);
}

export async function closeSession(
  req: CloseSessionRequest,
): Promise<CashierSnapshot> {
  return useLive ? httpApi.closeSession(req) : mockApi.closeSession(req);
}

/**
 * Introspection helper — exposes the currently active mode so debug panels
 * and QA tooling can surface the dispatcher choice without re-reading env.
 */
export function getCashierApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}
